import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { interval, Subject } from 'rxjs';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { AgendaItemModel } from '../../../models/agenda-item.model';
import { SignalRChannelEnum } from '../../../models/enums/signalr-channel.enum';
import { GeneralMeetingCheckInService } from '../../../services/general-meeting-check-in.service';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { SignalRService } from '../../../services/signalr.service';
import { VotingDialogComponent } from '../../dialogs/voting/voting-dialog.component';

@Component({
    selector: 'sl-live-voting',
    templateUrl: './live-voting.component.html',
    styleUrls: ['./live-voting.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LiveVotingComponent extends BaseComponent implements OnInit {
    readonly currentVoting = signal<AgendaItemModel[]>([]);
    readonly isLoading = signal(true);
    readonly isStatusCheckLoading = signal(true);
    readonly isDisconnected = signal(false);
    readonly errorMessage = signal('');
    readonly isCheckedIn = signal(false);

    private isModalOpen = false;
    private readonly onUserVoted = new Subject<void>();
    private readonly removeClosedItemsAfter = 60000; // in milliseconds.

    private readonly matDialog = inject(MatDialog);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly signalRService = inject(SignalRService);
    private readonly generalMeetingCheckinService = inject(GeneralMeetingCheckInService);

    ngOnInit(): void {
        const updateVotingInterval = interval(1000); // Every second we check if the list status has changed.
        const updateVotingSub = updateVotingInterval.subscribe(() => this.setupVoting(undefined, false));
        const msgSub = this.signalRService.onMessageReceived.subscribe(() => this.loadVoting());
        const discSub = this.signalRService.onDisconnected.subscribe(() => {
            this.errorMessage.set('disconnected');
            this.isDisconnected.set(true);
            // Let's retry to connect after 1 second
            setTimeout(() => this.signalRService.startConnection(SignalRChannelEnum.Voting), 1000);
            this.loadVoting();
        });
        const connSub = this.signalRService.onConnection.subscribe(error => {
            this.errorMessage.set(error);
            this.isDisconnected.set(!!error);
        });
        const userVotedSub = this.onUserVoted.subscribe(() => this.loadVoting());

        const checkInStatusSub = this.generalMeetingCheckinService.checkInStatusChange().subscribe(() => {
            this.loadVoting();
        });

        this.signalRService.startConnection(SignalRChannelEnum.Voting);
        this.addSubscriptions(msgSub, discSub, updateVotingSub, connSub, userVotedSub, checkInStatusSub);

        this.loadVoting();
    }

    vote(): void {
        this.openVoteModal();
    }

    votingClosed(items: AgendaItemModel[]): boolean {
        return items.every(i => !i.isVotingOpen);
    }

    getChildren(item: AgendaItemModel): AgendaItemModel[] {
        return this.currentVoting().filter(i => i.parentGmAgendaItemNumber === item.gmAgendaItemNumber);
    }

    hasChildren(item: AgendaItemModel): boolean {
        return this.getChildren(item).length > 0;
    }

    onCheckIn(): void {
        this.generalMeetingCheckinService.checkIn();
    }

    private loadVoting(): void {
        this.isLoading.set(true);
        this.isStatusCheckLoading.set(true);

        this.generalMeetingCheckinService.getStatus().subscribe(isCheckedIn => {
            this.isCheckedIn.set(isCheckedIn);
            this.isStatusCheckLoading.set(false);
        });

        this.generalMeetingsResource.getCurrentAgendaItemsForShareholder().subscribe(r => {
            this.setupVoting(r.value);
            this.isLoading.set(false);
        });
    }

    private setupVoting(items?: AgendaItemModel[], canOpenModal = true): void {
        const currentItems = !!items ? items : this.currentVoting();
        const aMinuteAgo = new Date(new Date().getTime() - this.removeClosedItemsAfter); // We get the current date and deduct one minute.
        this.currentVoting.set(
            currentItems
                .map((i, index, arr) => {
                    if (
                        i.isVotingOpen
                        || (!!i.votingStartedDateTime
                            && !!i.votingEndedDateTime
                            && (!i.resultAcceptedTime || i.resultAcceptedTime > aMinuteAgo))
                    ) {
                        return i;
                    } else {
                        const latestChild = arr
                            .filter(child => child.parentGmAgendaItemNumber === i.gmAgendaItemNumber)
                            .sort((a, b) =>
                                // Sort by (1) voting open, (2) votingStartedDateTime DESC.
                                b.isVotingOpen && a.isVotingOpen
                                    ? (b.votingStartedDateTime?.getTime() ?? 0) - (a.votingStartedDateTime?.getTime() ?? 0)
                                    : (b.isVotingOpen ? 1 : 0) - (a.isVotingOpen ? 1 : 0)
                            )[0];

                        if (latestChild) {
                            // Setting parents voting started time to that of first child for display purposes.
                            i.votingStartedDateTime = latestChild.votingStartedDateTime;
                            // Setting parents isVotingOpen if any of the children is open
                            i.isVotingOpen ||= latestChild.isVotingOpen;
                        }

                        return i;
                    }
                })
                .sort((a, b) =>
                    // Sort by (1) voting open, (2) votingStartedDateTime DESC.
                    b.isVotingOpen && a.isVotingOpen
                        ? (b.votingStartedDateTime?.getTime() ?? 0) - (a.votingStartedDateTime?.getTime() ?? 0)
                        : (b.isVotingOpen ? 1 : 0) - (a.isVotingOpen ? 1 : 0)
                )
        );

        if (
            canOpenModal
            && this.currentVoting().length > 0
            && this.isCheckedIn()
            && this.currentVoting().some(i => i.isVotingOpen && i.vote === 0)
            && !this.isModalOpen
        ) {
            // Open the modal only if there is something to vote on. Do not re-open the modal when an agenda-item has been closed
            // and there are still open agenda-items for which the user already voted
            this.openVoteModal();
        }
    }

    private openVoteModal(): void {
        this.isModalOpen = true;
        const dialogRef = this.matDialog.open(VotingDialogComponent, {
            data: this.onUserVoted,
            width: '600px',
            maxWidth: '95vw'
        });

        dialogRef.afterClosed().subscribe(() => (this.isModalOpen = false));
    }
}
